import { useEffect } from 'react';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ReactSelect, { SingleValue } from 'react-select';

import { useTypedDispatch } from '@app/store/hooks/useTypedDispatch';
import { baseApi } from '@app/store/index.api';
import { geoActions, GEO_OPTIONS, IGeo, useGeoValue } from '@entities/geo';
import { reactSelectStyles } from '@shared/components/Form';

import './geo-select.css';

const formatOptionLabel = ({ label, icon }: IGeo) => (
  <span className='geo-option-label'>
    <img className='geo-option-label__icon' src={icon} alt={label} />
    <span>{label}</span>
  </span>
);

export const GeoSelect = () => {
  const dispatch = useTypedDispatch();
  const currentGeo = useGeoValue();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const geoFromUrl = searchParams.get('geo') as IGeo['value'] | null;

    if (geoFromUrl && GEO_OPTIONS.some(o => o.value === geoFromUrl)) {
      if (geoFromUrl !== currentGeo) {
        dispatch(geoActions.setGeo(geoFromUrl));
      }
    } else {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('geo', currentGeo);
      navigate({ search: newParams.toString() }, { replace: true });
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (option: SingleValue<IGeo>) => {
    if (!option || option.value === currentGeo) return;

    dispatch(geoActions.setGeo(option.value));
    dispatch(baseApi.util.resetApiState());
    navigate({ pathname: location.pathname, search: `?geo=${option.value}` }, { replace: true });
  };

  const selectedOption = GEO_OPTIONS.find(o => o.value === currentGeo) ?? GEO_OPTIONS[0];

  return (
    <ReactSelect<IGeo>
      options={GEO_OPTIONS}
      value={selectedOption}
      onChange={handleChange}
      formatOptionLabel={formatOptionLabel}
      isSearchable={false}
      styles={{
        ...reactSelectStyles<IGeo>(),
        container: provided => ({ ...provided, width: '150px' }),
      }}
      menuPortalTarget={document.body}
    />
  );
};
